// GenerateBankDock — pill flutuante da geração de simulado a partir de caderno.
//
// Aparece quando há uma tarefa de geração ativa (bankGenTask) E o modal está
// fechado (bankGenModalOpen === false). Deixa o aluno fechar o modal e
// continuar usando o app: o progresso/resultado fica acompanhável aqui.
// [[project-caderno-bank-context]] [[feedback-user-clarity-context]]
//
// Posição: rodapé centralizado (bottom-center) pra não brigar com os docks de
// canto (player bottom-right, QuickStudy bottom-[5.5rem] right, InboxDock
// bottom-left).

import { useEffect, useRef } from "preact/hooks";
import {
  AlertTriangle,
  Loader2,
  Sparkles,
  X
} from "lucide-react";
import {
  bankGenModalOpen,
  bankGenTask,
  cancelBankGeneration,
  dismissBankGen
} from "./generateBankStore";
import { loadBanks, setActiveNav } from "./App";
import { openBank } from "./BankDetailView";

export function GenerateBankDock() {
  const task = bankGenTask.value;
  const modalOpen = bankGenModalOpen.value;

  // Quando a geração CONCLUI, atualiza a lista de bancos uma vez (pra quando o
  // aluno navegar pra Bancos já estar lá). Guarda o id tratado pra não repetir.
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (task?.status === "done" && task.result) {
      const id = task.result.bank.id;
      if (refreshedFor.current !== id) {
        refreshedFor.current = id;
        void loadBanks({ silent: true });
      }
    }
  }, [task?.status, task?.result?.bank.id]);

  // Esconde enquanto o modal está aberto (ele já mostra tudo) ou sem tarefa.
  if (!task || modalOpen) return null;

  function openCreated() {
    const id = task?.result?.bank.id;
    if (id) {
      setActiveNav("bancos");
      openBank(id);
    }
    dismissBankGen();
  }

  const base =
    "fixed bottom-4 left-1/2 z-[70] w-[min(420px,calc(100vw-1.5rem))] -translate-x-1/2 rounded-xl border bg-card shadow-2xl";

  if (task.status === "generating") {
    return (
      <div class={`${base} border-violet-500/40`}>
        <div class="flex items-start gap-3 px-4 py-3">
          <Loader2 className="mt-0.5 size-5 shrink-0 animate-spin text-violet-500" />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-bold">Gerando seu simulado…</p>
            <p class="mt-0.5 text-[11px] leading-snug text-muted-foreground">
              Pode continuar usando o app — avisamos no{" "}
              <strong>sininho</strong> quando ficar pronto. Não feche a aba do
              navegador.
            </p>
          </div>
          <button
            type="button"
            onClick={cancelBankGeneration}
            class="shrink-0 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-semibold text-muted-foreground hover:border-destructive/50 hover:text-destructive"
          >
            Interromper
          </button>
        </div>
      </div>
    );
  }

  if (task.status === "done" && task.result) {
    const r = task.result;
    return (
      <div class={`${base} border-emerald-500/40`}>
        <div class="flex items-start gap-3 px-4 py-3">
          <Sparkles className="mt-0.5 size-5 shrink-0 text-emerald-500" />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-bold">Simulado pronto!</p>
            <p class="mt-0.5 truncate text-[11px] text-muted-foreground">
              "{r.bank.name}" — {r.delivered} {r.delivered === 1 ? "questão" : "questões"}.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreated}
            class="shrink-0 rounded-md bg-violet-600 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-violet-700"
          >
            Abrir banco
          </button>
          <button
            type="button"
            onClick={dismissBankGen}
            aria-label="Dispensar"
            title="Dispensar"
            class="grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    );
  }

  if (task.status === "error") {
    return (
      <div class={`${base} border-destructive/40`}>
        <div class="flex items-start gap-3 px-4 py-3">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-bold text-destructive">
              Falha ao gerar simulado
            </p>
            <p class="mt-0.5 text-[11px] leading-snug text-muted-foreground">
              {task.error ?? "Tente de novo pelo botão Gerar simulado no caderno."}
            </p>
          </div>
          <button
            type="button"
            onClick={dismissBankGen}
            aria-label="Dispensar"
            title="Dispensar"
            class="grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
